import { FormInput, PopupBody, PopupFooter, PopupHeader, PopupWrapper, VerticalForm } from '@/components'
import { PermissionTypes } from '@/constants/permissions'
import { ChangePasswordForcefullyRequest, ChangePasswordRequest } from '@/helpers/api/WebApiClient'
import { personalService } from '@/services/PersonalService'
import { userService } from '@/services/UserService'
import { runWithToast } from '@/helpers/asyncToast.helper'
import { messageHelper } from '@/helpers/message.helper'
import { usePermission } from '@/hooks/usePermission'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

export interface ChangeUserPasswordProps {
	/** When provided, admin flow (change password for another user). When omitted, self flow (change own password). */
	userId?: string
	/** For admin flow: callback when modal should close */
	onUserActionClick?: (isChanged: boolean) => void
	/** 'popup' for modal (admin), 'inline' for card layout (profile) */
	variant?: 'popup' | 'inline'
}

const adminPasswordResolver = yupResolver(
	yup.object({
		newPassword: yup.string().required('New password is required'),
		confirmNewPassword: yup.string().required('Confirm password is required').oneOf([yup.ref('newPassword')], 'Passwords must match'),
	})
)

const selfPasswordResolver = yupResolver(
	yup.object({
		password: yup.string().required('Old password is required'),
		newPassword: yup.string().required('New password is required').notOneOf([yup.ref('password')], 'New password cannot be the same as old password'),
		confirmNewPassword: yup.string().required('Confirm password is required').oneOf([yup.ref('newPassword')], 'Passwords must match'),
	})
)

const ChangeUserPassword: React.FC<ChangeUserPasswordProps> = (props) => {
	const { t } = useTranslation()
	const { userHasPermission } = usePermission()
	const isAdminMode = !!props.userId
	const variant = props.variant ?? (isAdminMode ? 'popup' : 'inline')

	const handleAdminSubmit = async (formData: ChangePasswordForcefullyRequest) => {
		await runWithToast(() => userService.changePasswordForcefully(props.userId!, formData), {
			onSuccess: (response) => {
				messageHelper.showSuccess(response)
				props.onUserActionClick?.(false)
			},
		})
	}

	const handleSelfSubmit = async (formData: ChangePasswordRequest) => {
		await runWithToast(() => personalService.changePassword(formData), {
			onSuccess: (response) => messageHelper.showSuccess(response),
		})
	}

	const PASSWORD_STRENGTH_LEVELS = [
		{ label: 'Very weak', color: '#e11d2a' },
		{ label: 'Weak', color: '#ff7e57' },
		{ label: 'Fair', color: '#ffb84d' },
		{ label: 'Good', color: '#8bd450' },
		{ label: 'Strong', color: '#2fd47a' },
	]

	const scorePassword = (password: string) => {
		if (!password) return 0
		let score = 0
		if (password.length >= 8) score++
		if (password.length >= 12) score++
		if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
		if (/\d/.test(password)) score++
		if (/[^A-Za-z0-9]/.test(password)) score++
		return Math.min(score, PASSWORD_STRENGTH_LEVELS.length) - 1
	}

	const PasswordStrengthMeter = () => {
		const { watch } = useFormContext<ChangePasswordForcefullyRequest | ChangePasswordRequest>()
		const password = watch('newPassword') ?? ''
		if (!password) return null

		const levelIndex = Math.max(0, scorePassword(password))
		const level = PASSWORD_STRENGTH_LEVELS[levelIndex]

		return (
			<div className="mt-2">
				<div className="flex h-1.5 w-full gap-1 overflow-hidden">
					{PASSWORD_STRENGTH_LEVELS.map((segment, idx) => (
						<div
							key={segment.label}
							className="h-full flex-1 rounded-full transition-colors duration-300"
							style={{ backgroundColor: idx <= levelIndex ? level.color : '#5a211d' }}
						/>
					))}
				</div>
				<p className="mt-1.5 text-xs font-medium" style={{ color: level.color }}>
					{level.label} password
				</p>
			</div>
		)
	}

	if (isAdminMode && variant === 'popup') {
		return (
			<PopupWrapper variant="default">
				<PopupHeader title={t('Manage.User.ChangeUserPassword_Heading', 'Change User Password')} onClose={() => props.onUserActionClick?.(false)} />
				<VerticalForm<any> onSubmit={handleAdminSubmit} resolver={adminPasswordResolver}>
					<PopupBody>
						<div className="grid lg:grid-cols-2 gap-6">
							<div>
								<FormInput label={t('Manage.User.ChangeUserPassword.New_Password', 'New Password')} labelClassName="form-label" containerClass="form-field" type="password" name="newPassword" className="form-input" key="newPassword" required />
								<PasswordStrengthMeter />
							</div>
							<FormInput label={t('Manage.User.ChangeUserPassword.Confirm_Password', 'Confirm Password')} labelClassName="form-label" containerClass="form-field" type="password" name="confirmNewPassword" className="form-input" key="confirmNewPassword" required />
						</div>
					</PopupBody>
					<PopupFooter>
						<button type="button" className="btn btn-secondary" onClick={() => props.onUserActionClick?.(false)}>
							{t('Manage.User.ChangeUserPassword_Close', 'Close')}
						</button>
						{userHasPermission(PermissionTypes.Permissions_Users_Update) || userHasPermission(PermissionTypes.Permissions_Tenants_Update) ? (
							<button className="btn btn-primary" type="submit">
								{t('Manage.User.ChangeUserPassword_Save', 'Update Password')}
							</button>
						) : null}
					</PopupFooter>
				</VerticalForm>
			</PopupWrapper>
		)
	}

	return (
		<div className="card p-6 mb-6">
			<h4 className="card-title mb-1">{t('Manage.Profile.Edit_ChangePassword', 'Change Password')}</h4>
			<VerticalForm<any> onSubmit={handleSelfSubmit} resolver={selfPasswordResolver}>
				<div className="grid lg:grid-cols-1 gap-6 pt-5">
					<FormInput label={t('Manage.Profile.Edit_OldPassword', 'Old Password')} labelClassName="form-label" containerClass="form-field" type="password" name="password" placeholder={t('Manage.Profile.Edit.Placeholder_OldPassword', 'Old Password')} className="form-input" key="password" required />
					<div>
						<FormInput label={t('Manage.Profile.Edit_NewPassword', 'New Password')} labelClassName="form-label" containerClass="form-field" type="password" name="newPassword" placeholder={t('Manage.Profile.Edit.Placeholder_NewPassword', 'New Password')} className="form-input" key="newPassword" required />
						<PasswordStrengthMeter />
					</div>
					<FormInput label={t('Manage.Profile.Edit_ConfirmPassword', 'Confirm Password')} labelClassName="form-label" containerClass="form-field" type="password" name="confirmNewPassword" placeholder={t('Manage.Profile.Edit.Placeholder_ConfirmPassword', 'Confirm Password')} className="form-input" key="confirmNewPassword" required />
					<button className="btn btn-primary w-full mt-5" type="submit">
						{t('Manage.Profile.Edit_BtnChangePassword', 'Change Password')}
					</button>
				</div>
			</VerticalForm>
		</div>
	)
}

export default ChangeUserPassword
